import type { AuthMeResponse } from "@/definitions/types";
import type { Ref } from "vue";
import axios, {
  type AxiosError,
  type AxiosResponse,
  CanceledError,
  type InternalAxiosRequestConfig,
  type Method,
} from "axios";
import { stringify } from "qs";
import { toast } from "vue-sonner";
import { API_HOST } from "@/constants/envs";
import { READ_ONLY_REASON } from "@/definitions/roleNotice";
import { useAuthStore } from "@/stores/auth";
import {
  cancelWriteIfRoleLowered,
  getValidatedAccessToken,
  signOut,
} from "@/utils/commands";
import { logger } from "@/utils/logger";
import { reportUnconfirmedOutcome } from "@/utils/sentry";
import { assertTabSession } from "@/utils/session";
import { toastError } from "@/utils/toaster";

export const axiosInstance = axios.create({
  baseURL: API_HOST,
  timeout: 30000, // 30 seconds
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
  },
});

export interface ApiResponse<T> extends AxiosResponse<T> {
  success: boolean;
}

export interface ApiOptions {
  refLoading?: Ref<boolean>;
  alert?: boolean;
  successAlert?: boolean;
  successMessage?: string;
  failureAlert?: boolean;
  failureMessage?: string;
  /**
   * 인터셉터 단계의 토스트(네트워크 오류 등)까지 완전히 끈다.
   * 사용자가 유발하지 않은 백그라운드 호출(앱 부팅 시 `GET auth/me` 등)에만 쓴다.
   */
  silent?: boolean;
  /**
   * 호출부가 요청을 끊는 신호(70차 cr-fe-common M6 — AI 검토 「취소」 등). 끊으면 HTTP 요청이 실제로 중단되고 axios 취소 오류로 끝난다
   * (화면 이동 · 워크스페이스 전환 취소와 같은 길 — `pendingRequests` 의 요청별 controller 에 이어 붙인다).
   */
  signal?: AbortSignal;
  /**
   * 422 입력 검증 실패의 칸 라벨(75차 · 백로그 B77) — 기본 토스트(`failureMessage` 없음)가 「입력값을 확인해 주세요 — 원인.」 대신
   * 「라벨(N번째): 원인.」 으로 어느 칸인지 말한다. 키는 `validationFailureText` 와 같다(정확한 field → `invites.*.email` → 마지막 이름).
   * 주지 않으면 지금 그대로. `failureMessage` 가 있으면 그 문구가 이긴다.
   */
  fieldLabels?: Record<string, string>;
  /**
   * 읽기 성격의 POST(역할 4종이 모두 허용하는 AI 도우미 질문 등)에 준다(90차 AU90-07). 재발급이 역할 강등을 드러내도 「권한이 줄어 변경 요청을
   * 보내지 않았습니다」 로 취소하지 않고 새 토큰으로 다시 보낸다 — 서버가 낮아진 역할로도 통과시키는 요청이다. 기본 false(쓰기는 취소).
   * 검토자 예외(VIEWER 검토자의 분석 저장 · 검토 응답)는 이 옵션 없이 한 번 취소된 뒤 다시 누르면 통과하는 것으로 둔다.
   */
  readLike?: boolean;
}

/** `silent` 옵션을 요청 config 에 실어 인터셉터에서 읽는다(커스텀 헤더가 아니므로 CORS 영향 없음). */
interface SilentConfig {
  silent?: boolean;
  readLike?: boolean;
}

function isSilent(config?: unknown): boolean {
  return (config as SilentConfig | undefined)?.silent === true;
}

function isReadLike(config?: unknown): boolean {
  return (config as SilentConfig | undefined)?.readLike === true;
}

/**
 * `must-renew` 로 재발급한 뒤 다시 보낸 요청의 표시(90차 AU90-04). 다시 보낸 요청이 또 `must-renew` 를 받으면 재발급을 되풀이하지 않고
 * 세션 만료(401)로 끝낸다 — 서버의 점조회 판정과 재발급 판정이 한 번이라도 어긋나면 탭마다 초당 여러 번 재발급(리프레시 사용자 행 잠금)이
 * 나기 때문이다. 정상 경로(재발급 토큰이 맞음)는 두 번째 `must-renew` 가 없어 영향이 없다.
 */
interface RenewRetriedConfig {
  _renewRetried?: boolean;
}

/**
 * 인터셉터 단계에서 요청을 끝낼 때 돌려주는 실패 응답.
 * - 헬퍼(`getApi` 등)는 **절대 undefined 를 반환하지 않는다.**
 *   호출부가 `const { success } = await getApi(...)` 처럼 구조분해해도 안전해야 한다.
 * - 형태는 `catchError()` 가 만드는 실패 응답과 동일하다.
 */
/** 이 계정(워크스페이스 구성원)의 쓰기가 403 `UNKNOWN_AUTHORITY` 로 거절됐는가. 운영자(Admin) 화면의 403 은 역할 문제가 아니라 건너뛴다. */
function isForbiddenWrite(error: AxiosError): boolean {
  const response = error.response;
  if (response?.status !== 403) return false;
  const method = error.config?.method?.toLowerCase();
  if (!method || method === "get") return false;
  const code = (response.data as { code?: unknown } | undefined)?.code;
  return code === "UNKNOWN_AUTHORITY" && Boolean(useAuthStore().workspace.id);
}

/** 403 뒤 역할 재동기화가 진행 중인가(쓰기를 연달아 눌러도 `auth/me` 는 한 번씩만) */
let roleResyncInFlight = false;

/** `GET auth/me` 로 서버 기준 역할을 받아 스토어에 반영한다. 실패해도 조용히 넘어간다 — 사용자가 누른 동작이 아니다. */
async function resyncRoleAfterForbiddenWrite(): Promise<void> {
  if (roleResyncInFlight) return;
  roleResyncInFlight = true;
  try {
    const response = await getApi<AuthMeResponse>("api/v1/auth/me", {
      failureAlert: false,
      silent: true,
    });
    if (response.success && response.data) {
      useAuthStore().applyMe(response.data);
    }
  } catch (e: unknown) {
    logger.warn("Role resync after forbidden write failed:", e);
  } finally {
    roleResyncInFlight = false;
  }
}

function toFailureResponse<T>(error: AxiosError): ApiResponse<T> {
  return {
    status: error.response?.status ?? 0,
    statusText: error.response?.statusText ?? "",
    config: error.config as InternalAxiosRequestConfig,
    headers: error.response?.headers ?? {},
    request: error.request,
    data: error.response?.data as T,
    success: false,
  };
}

export const pendingRequests: Map<string, AbortController> = new Map();

/** `pendingRequests` 중 쓰기(GET 이 아닌) 요청 ID — 역할 강등 뒤 자동 다시 불러오기가 진행 중 쓰기를 끊지 않게 본다(89차 B100 ⓒ R2) */
const pendingWriteIds: Set<string> = new Set();

/** 서버로 나간 뒤 아직 끝나지 않은 쓰기(POST · PUT · PATCH · DELETE)가 있는가 */
export function hasPendingWrites(): boolean {
  return pendingWriteIds.size > 0;
}

/**
 * 호출부 신호(`ApiOptions.signal`)에 단 abort 리스너의 해제 함수(요청 ID 별). `{ once: true }` 는 abort 가 나야 풀리므로, 화면 단위 signal 을
 * 여러 요청에 재사용하면 끝난 요청의 controller 를 붙든다(cr-fix F9). 응답 · 오류 인터셉터가 요청이 끝날 때 부른다 — 화면 이동으로
 * `pendingRequests` 를 비워 끊은 요청도 취소 오류로 오류 인터셉터를 지나므로 함께 풀린다.
 */
const callerSignalReleases: Map<string, () => void> = new Map();

/** 끝난 요청의 추적을 지운다(`pendingRequests` 항목 · 호출부 신호 리스너). */
function releaseRequest(requestId: string | undefined): void {
  if (!requestId) return;
  pendingRequests.delete(requestId);
  pendingWriteIds.delete(requestId);
  callerSignalReleases.get(requestId)?.();
  callerSignalReleases.delete(requestId);
}

/**
 * 보내기 전에 막은 요청(세션 안내 · 재발급 중 워크스페이스 전환)을 화면 이동으로 끊은 요청과 같은 모양으로 끝낸다 — 중단된 `signal` 을 실은
 * config 를 단다. 화면들은 "status 0 + 중단된 signal" 을 취소로 보고 조용히 넘기고, 그 밖의 status 0 은 연결 끊김(결과 불명)으로 읽어
 * "저장됐는지 확인하지 못했다" 를 띄운다(면접 콘솔 `isCanceledRequest` 등).
 */
function canceledRequest(
  config: InternalAxiosRequestConfig,
  message: string | undefined,
): CanceledError<unknown> {
  const controller = new AbortController();
  controller.abort(message);
  config.signal = controller.signal;
  return new CanceledError(message, config);
}

const DEFAULT_SUCCESS_MESSAGES: Record<string, string> = {
  get: "조회되었습니다.",
  post: "등록되었습니다.",
  put: "수정되었습니다.",
  patch: "수정되었습니다.",
  delete: "삭제되었습니다.",
} as const;

axiosInstance.interceptors.request.use(
  async function (config) {
    if (!config.headers) {
      logger.error("Failed to set 'request headers' : headers is not exist");
      return {} as InternalAxiosRequestConfig;
    }
    let accessToken: string;
    try {
      accessToken = await getValidatedAccessToken(config.method);
      // 재발급을 기다리는 사이 세션 만료 안내가 떴거나 다른 탭이 토큰을 바꿨으면 보내지 않는다(30차 S30-01 · S30-02).
      // 토큰 확인은 `getValidatedAccessToken` 첫 줄에서도 한다 — 다른 탭 토큰을 이 탭 워크스페이스로 재발급하지 않게.
      assertTabSession();
    } catch (e: unknown) {
      if (axios.isCancel(e)) throw canceledRequest(config, e.message);
      throw e;
    }
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // 요청 ID 생성
    const requestId = crypto.randomUUID();
    const controller = new AbortController();

    // 호출부 신호(`ApiOptions.signal`)가 있으면 요청별 controller 에 잇는다 — 화면 이동 취소(`pendingRequests`)와 호출부 취소 둘 다 먹는다.
    const callerSignal = config.signal as AbortSignal | undefined;
    if (callerSignal?.aborted) {
      controller.abort(callerSignal.reason);
    } else if (callerSignal?.addEventListener) {
      const onCallerAbort = () => {
        controller.abort(callerSignal.reason);
      };
      callerSignal.addEventListener("abort", onCallerAbort, { once: true });
      callerSignalReleases.set(requestId, () => {
        callerSignal.removeEventListener("abort", onCallerAbort);
      });
    }
    config.signal = controller.signal;
    config.headers.requestId = requestId;

    pendingRequests.set(requestId, controller);
    if (config.method && config.method.toLowerCase() !== "get") {
      pendingWriteIds.add(requestId);
    }

    return config;
  },
  async function (error) {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  function (response) {
    releaseRequest(response.config.headers.requestId as string | undefined);

    if (!response) {
      toastError("응답이 없습니다.");
    }
    return { ...response, success: Math.floor(response.status / 100) === 2 };
  },
  async function (error: AxiosError) {
    releaseRequest(error.config?.headers?.requestId as string | undefined);

    // 라우트/워크스페이스 전환 시 우리가 직접 abort 한 요청은 오류가 아니다.
    // 토스트와 error 로그를 건너뛰고 debug 레벨로만 남긴다.
    if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
      logger.debug("Request canceled:", error.message);
      return Promise.reject(error);
    }

    logger.error(error.message);

    if (error.message === "Network Error") {
      if (!isSilent(error.config)) {
        // 쓰기는 서버가 처리한 뒤 응답만 끊겼을 수 있다 — 「다시 하라」 고 하지 않고 먼저 확인하게 한다(JR83-02).
        toastFailure(
          isConnectionLost(error)
            ? CONNECTION_LOST_MESSAGE
            : CONNECTION_FAILED_MESSAGE,
        );
      }
      return toFailureResponse(error);
    }
    if (error.response) {
      if (
        error.response.headers.token === "must-renew" &&
        !(error.response.config as RenewRetriedConfig)._renewRetried
      ) {
        const { reIssueAccessToken } = useAuthStore();
        try {
          const heldToken = String(
            error.response.config.headers.Authorization ?? "",
          ).replace(/^Bearer /, "");
          await reIssueAccessToken();
          const renewedToken = await getValidatedAccessToken();
          error.response.config.headers.Authorization = renewedToken;
          // 재발급이 역할 강등을 드러냈으면 쓰기는 다시 보내지 않는다(83차 JR83-05)
          if (!isReadLike(error.response.config)) {
            cancelWriteIfRoleLowered(
              heldToken,
              renewedToken,
              error.response.config.method,
            );
          }
        } catch (e: unknown) {
          // 재발급을 막았으면(세션 안내 · 재발급 중 워크스페이스 전환) 다시 보내지 않고 취소로 끝낸다.
          if (axios.isCancel(e)) {
            throw canceledRequest(error.response.config, e.message);
          }
          throw e;
        }
        (error.response.config as RenewRetriedConfig)._renewRetried = true;
        return axiosInstance.request(error.response.config);
      }
      // 쓰기가 403 「알 수 없는 권한」 으로 거절됐다 — 화면 역할이 DB 역할보다 높게 남은 것이다(권한의 정본은 DB, AU90-03 보조).
      // 서버 원문 대신 읽기 전용 사유를 보이고, 서버 기준 역할로 다시 맞춘다(`applyMe` — 낮아졌으면 쓰기를 곧바로 잠근다).
      if (isForbiddenWrite(error)) {
        error.response.data = {
          ...(error.response.data as object),
          message: READ_ONLY_REASON,
        };
        void resyncRoleAfterForbiddenWrite();
      }
      // 이미 한 번 재발급해 다시 보낸 요청이 또 `must-renew` 면 이 분기(401)로 끝난다(AU90-04).
      if ([401].includes(error.response.status)) {
        // 열어 둔 화면을 실어 보내 다시 로그인하면 그 화면으로 돌아오게 한다(25차 M25-01 수정 라운드).
        // 저장하지 않은 편집이 있으면 바로 보내지 않고 만료 안내부터 띄운다(30차 S30-02, `signOut` → `expireSession`).
        await signOut();
        return toFailureResponse(error);
      }
    }
    logger.error(error);
    return Promise.reject(error);
  },
);

function getDefaultOptions(method: Method, options?: ApiOptions): ApiOptions {
  const defaultOpts = {
    successMessage: undefined,
    refLoading: undefined,
    alert: undefined,
    successAlert: method !== "get",
    failureAlert: true,
    failureMessage: undefined,
  };

  const mergedOptions = {
    ...defaultOpts,
    ...options,
  };

  return {
    ...mergedOptions,
    successAlert: mergedOptions.alert ?? mergedOptions.successAlert,
    failureAlert: mergedOptions.alert ?? mergedOptions.failureAlert,
  };
}

function getPromiseMessage<T>(
  successMessage: string,
  successAlert?: boolean,
  failureAlert?: boolean,
  failureMessage?: string,
  fieldLabels?: Record<string, string>,
) {
  const failureText = (e: unknown): string => {
    logger.warn(e);
    // 결과를 모르는 실패는 호출부 문구보다 먼저 쓴다. `failureMessage` 가 "저장하지 못했습니다" 처럼 단정하면
    // 실제로는 서버가 처리를 끝낸 경우를 잘못 알린다.
    if (isUnconfirmedOutcome(e)) {
      return unconfirmedOutcomeMessage(e);
    }
    if (axios.isAxiosError(e)) {
      // AI 생성 상한(429)은 호출부 문구보다 먼저 — 「N초 뒤」 · 「내일」 이 원인이다(70차 CO70-03).
      const limitMessage =
        e.response && isAiGenerationLimitFailure(e.response)
          ? llmFailureMessage(e.response)
          : null;
      // 입력 검증 실패(422)는 영문 pydantic 원문 대신 칸 · 원인을 한국어로(72차 CW72-05).
      return (
        limitMessage ??
        failureMessage ??
        (isValidationFailure(e)
          ? validationFailureText(e, fieldLabels)
          : e.response?.data?.message) ??
        e.message
      );
    }
    return (
      failureMessage ??
      (e instanceof Error ? e.message : "알 수 없는 오류가 발생했습니다")
    );
  };

  return {
    success: successAlert
      ? ({ success }: ApiResponse<T>): string | undefined => {
          if (successAlert && success) {
            return successMessage;
          }
          return undefined;
        }
      : undefined,
    error: failureAlert
      ? (e: unknown) => {
          const message = failureText(e);
          // 같은 문장은 한 장으로 합친다(JR83-09) — 토스트 id 를 문장으로 둔다
          return { message, id: message };
        }
      : undefined,
  };
}

/**
 * AI 생성 실패 응답인지. 백엔드는 LLM 호출이 결과를 만들지 못하면 가짜 결과 대신
 * **503 `{ code: "LLM_<원인>", message, data }`** 로 응답한다(`app/main.py` 의 `LLMGenerationError` 핸들러).
 * 원인은 `NOT_CONFIGURED`(공급자 미연결·키 오류) · `QUOTA_EXCEEDED`(크레딧 소진) · `RATE_LIMITED` ·
 * `TIMEOUT` · `INVALID_RESPONSE` · `PROVIDER_ERROR` · `INPUT_REJECTED`(입력이 너무 길거나 정책에 걸림 — `isLlmInputRejectedFailure`)이고,
 * `message` 가 사용자에게 보일 한국어 원인 문구다.
 */
function isLlmFailure(status: number, body: unknown): boolean {
  const code = (body as { code?: unknown } | undefined)?.code;
  return status === 503 && typeof code === "string" && code.startsWith("LLM_");
}

/**
 * AI 생성 호출 상한 초과인지. 백엔드는 LLM 을 부르는 사용자 라우트의 호출 횟수를 사용자 분당 · 사용자 하루 · 워크스페이스 하루로
 * 세어 넘으면 **429 `{ code: "AI_GENERATION_<MINUTE|USER_DAILY|WORKSPACE_DAILY>_LIMIT_EXCEEDED", message, data: { limit } }`**
 * 로 거절한다. `message` 가 사용자에게 보일 한국어 문구다.
 */
function isAiGenerationLimit(status: number, body: unknown): boolean {
  const code = (body as { code?: unknown } | undefined)?.code;
  return (
    status === 429 &&
    typeof code === "string" &&
    code.startsWith("AI_GENERATION_") &&
    code.endsWith("_LIMIT_EXCEEDED")
  );
}

/**
 * 실패 응답이 AI 생성 호출 상한 초과(429 `AI_GENERATION_*_LIMIT_EXCEEDED`)인지. 일괄 경로는 하루 상한이면 `isUnrecoverableLlmFailure` 로 멈추고
 * 분당 상한이면 기다렸다 잇지만(70차 CO70-04), 이 실패는
 * 관리자가 AI 설정을 고쳐서 풀리는 것이 아니라 시간이 지나야(분당 · 하루) 풀리므로 "관리자 조치" 안내를 붙이지 않는 화면이 이 함수로 가른다.
 */
export function isAiGenerationLimitFailure(response: {
  status?: number;
  data?: unknown;
}): boolean {
  return isAiGenerationLimit(response.status ?? 0, response.data);
}

/**
 * 실패 응답이 AI 생성 **분당** 상한 초과(429 `AI_GENERATION_MINUTE_LIMIT_EXCEEDED`)인지. 몇 초 뒤(`retryAfterSeconds`) 같은 요청이 다시 받아진다 —
 * 일괄 경로는 멈추지 않고 그만큼 기다렸다 같은 대상을 다시 보낸다(70차 CO70-04, `operationsBulk.ts` 의 `runBulk` · `waitRetryAfter`).
 */
export function isAiGenerationMinuteLimitFailure(response: {
  status?: number;
  data?: unknown;
}): boolean {
  return (
    isAiGenerationLimit(response.status ?? 0, response.data) &&
    (response.data as { code?: unknown }).code ===
      "AI_GENERATION_MINUTE_LIMIT_EXCEEDED"
  );
}

/** 하루 상한 초과(사용자 · 워크스페이스)는 오늘 다시 보내도 거절된다. 분당 상한만 잠시 뒤 풀린다. */
function isAiGenerationDailyLimitCode(code: unknown): boolean {
  return (
    code === "AI_GENERATION_USER_DAILY_LIMIT_EXCEEDED" ||
    code === "AI_GENERATION_WORKSPACE_DAILY_LIMIT_EXCEEDED"
  );
}

/**
 * 실패 응답이 AI 생성 실패(503 `LLM_*`)나 AI 생성 호출 상한 초과(429 `AI_GENERATION_*_LIMIT_EXCEEDED`)면 서버 문구를,
 * 아니면 null 을 돌려준다.
 * `failureAlert: false` 로 부르고 실패 안내를 직접 그리는 화면이 도메인 문구보다 이 문구를 먼저 쓴다
 * (크레딧 소진·공급자 미연결을 "잠시 후 다시 시도" 같은 일반 문구로 덮으면 원인이 가려진다).
 * 상한 초과는 서버 문구가 없을 때 "잠시 뒤" · "내일" 로 갈라 안내한다.
 * 분당 상한은 남은 초(`retryAfterSeconds`)가 있으면 서버 고정 문구(「1분 뒤」) 대신 「N초 뒤」 로 다시 쓴다 — AI 도우미와 같은 문장(70차 CO70-03).
 */
export function llmFailureMessage(response: {
  status?: number;
  data?: unknown;
  headers?: unknown;
}): string | null {
  if (isAiGenerationLimit(response.status ?? 0, response.data)) {
    const body = response.data as { code?: unknown; message?: unknown };
    const seconds = isAiGenerationMinuteLimitFailure(response)
      ? retryAfterSeconds(response)
      : null;
    if (seconds !== null) {
      return `AI 생성 요청이 너무 잦습니다. ${String(seconds)}초 뒤 다시 시도해 주세요.`;
    }
    if (typeof body.message === "string" && body.message !== "") {
      return body.message;
    }
    return isAiGenerationDailyLimitCode(body.code)
      ? "오늘 사용할 수 있는 AI 생성 횟수를 모두 썼습니다. 내일 다시 시도해 주세요."
      : "AI 생성 요청이 너무 잦습니다. 잠시 뒤 다시 시도해 주세요.";
  }
  if (!isLlmFailure(response.status ?? 0, response.data)) return null;
  const message = (response.data as { message?: unknown }).message;
  if (typeof message === "string" && message !== "") return message;
  // 입력이 원인인 거절은 다시 시도해도 같은 결과라 재시도를 권하지 않는다(26차 E26-05).
  return isLlmInputRejectedFailure(response)
    ? "입력 내용 때문에 AI 가 처리하지 못했습니다. 입력한 내용(자료 · 문서)을 확인해 고친 뒤 다시 실행해 주세요."
    : "AI 생성에 실패했습니다. 잠시 뒤 다시 시도해 주세요.";
}

/**
 * 입력 내용이 원인인 AI 거절(503 `LLM_INPUT_REJECTED` — 입력이 너무 길거나 정책에 걸림)인지. 같은 입력으로는 다시 해도 같은 결과라
 * 재시도 버튼 · 문구를 두지 않는다. 일괄 처리는 **멈추지 않고** 그 항목만 실패로 센다(`isUnrecoverableLlmFailure` 와 다르다 — 다른 항목의 입력은 괜찮을 수 있다).
 */
export function isLlmInputRejectedFailure(response: {
  status?: number;
  data?: unknown;
}): boolean {
  return (
    isLlmFailure(response.status ?? 0, response.data) &&
    (response.data as { code?: unknown }).code === "LLM_INPUT_REJECTED"
  );
}

/**
 * AI 생성 실패의 서버 원인 문구. 503 `LLM_*`(공급자 호출 실패)와 400 `LLM_GENERATION_FAILED`(결과가 불완전해 저장하지
 * 않음 — `data.target` 으로 대상이 온다) 모두 `message` 가 사용자에게 보일 한국어 문구다. 그 밖의 실패는 null.
 * 호출별 토스트를 끈 일괄 경로가 요약에 원인을 붙일 때 쓴다.
 */
export function aiGenerationFailureMessage(response: {
  status?: number;
  data?: unknown;
}): string | null {
  const llmMessage = llmFailureMessage(response);
  if (llmMessage) return llmMessage;
  const body = response.data as
    { code?: unknown; message?: unknown } | undefined;
  return response.status === 400 &&
    body?.code === "LLM_GENERATION_FAILED" &&
    typeof body.message === "string" &&
    body.message !== ""
    ? body.message
    : null;
}

/**
 * 다시 시도해도 풀리지 않는 AI 생성 실패 코드(운영자가 키 · 크레딧을 조치해야 풀리거나, 하루 상한이 풀릴 때까지 기다려야 한다).
 * 여러 대상을 이어 부르는 일괄 경로는 이 코드를 받으면 남은 대상을 보내지 않고 멈춘다 — 보내 봐야 모두 같은 이유로 실패한다.
 * 하루 상한 초과(429 `AI_GENERATION_*_DAILY_LIMIT_EXCEEDED`)도 같다. **분당 상한은 넣지 않는다**(70차 CO70-04) — 몇 초 뒤 풀리므로
 * 일괄 경로는 `retryAfterSeconds` 만큼 기다렸다 같은 대상을 다시 보낸다(`isAiGenerationMinuteLimitFailure`). LLM 전에 끝나는 도메인 400 도
 * 분당 창을 채워, 예전처럼 멈추면 70명 일괄에서 61번째부터 모두 「미처리」 가 됐다.
 */
export function isUnrecoverableLlmCode(code: unknown): boolean {
  return (
    code === "LLM_QUOTA_EXCEEDED" ||
    code === "LLM_NOT_CONFIGURED" ||
    isAiGenerationDailyLimitCode(code)
  );
}

/**
 * 실패 응답이 다시 시도해도 풀리지 않는 AI 생성 실패(503 `LLM_QUOTA_EXCEEDED` · `LLM_NOT_CONFIGURED`, 429 하루 상한 초과)인지
 */
export function isUnrecoverableLlmFailure(response: {
  status?: number;
  data?: unknown;
}): boolean {
  const status = response.status ?? 0;
  const code = (response.data as { code?: unknown } | undefined)?.code;
  return (
    (isLlmFailure(status, response.data) ||
      isAiGenerationLimit(status, response.data)) &&
    isUnrecoverableLlmCode(code)
  );
}

/**
 * 429 응답이 알려 준 "몇 초 뒤 다시" 값. 본문 `data.retryAfter`(숫자 · 숫자 문자열)를 먼저 읽고, 없으면 `Retry-After` 헤더(초 단위)를 읽는다.
 * 둘 다 없거나 0 이하면 null — 호출부는 "잠시 뒤" 로 안내한다. 소수는 올린다.
 * 서버 본문 `data` 는 `{ code, message, data: { limit, retryAfter } }` 중 안쪽 객체도 허용한다.
 */
export function retryAfterSeconds(response: {
  data?: unknown;
  headers?: unknown;
}): number | null {
  const toSeconds = (value: unknown): number | null => {
    const n = typeof value === "string" ? Number(value.trim()) : value;
    return typeof n === "number" && Number.isFinite(n) && n > 0
      ? Math.ceil(n)
      : null;
  };
  const body = response.data as
    { retryAfter?: unknown; data?: { retryAfter?: unknown } } | undefined;
  const fromBody = toSeconds(body?.data?.retryAfter ?? body?.retryAfter);
  if (fromBody !== null) return fromBody;
  const headers = response.headers as
    { get?: (name: string) => unknown; [key: string]: unknown } | undefined;
  const header =
    typeof headers?.get === "function"
      ? headers.get("retry-after")
      : (headers?.["retry-after"] ?? headers?.["Retry-After"]);
  return toSeconds(header);
}

/**
 * 입력 검증 실패(422) 한 건. 서버(`app/main.py` 의 `RequestValidationError` 핸들러)가 `data.errors` 로 싣는다(72차 CW72-01).
 * - `field`: `loc` 에서 `body` · `query` · `path` 접두를 뺀 점 표기(camelCase 별칭 그대로, 예 `invites.3.email`). 루트 검증자는 `""`.
 * - `type`: pydantic 오류 종류(`string_too_long` · `missing` · `value_error` …). `limit`: 길이 · 범위 상한/하한이 있을 때만.
 * 서버는 입력 원문 · 영문 `msg` 를 싣지 않는다 — 화면 문구는 `type` · `limit` 으로만 만든다.
 */
export type ValidationIssue = {
  field: string;
  path: (string | number)[];
  type: string;
  limit?: number;
};

/** `data.errors` 가 없는 422(옛 서버)의 안내. 원인을 길이 초과로 단정하지 않는다(72차 CW72-04). */
export const VALIDATION_FAILURE_FALLBACK =
  "입력값을 확인해 주세요(길이 · 형식 · 필수 항목).";

/** AxiosError 든 실패 응답(`failureAlert: false` 로 받은 `ApiResponse`)이든 `{ status, data }` 로 읽는다. */
function failureResponseOf(
  e: unknown,
): { status?: number; data?: unknown } | undefined {
  if (axios.isAxiosError(e)) return e.response;
  if (typeof e === "object" && e !== null && "status" in e) {
    return e as { status?: number; data?: unknown };
  }
  return undefined;
}

/** 실패가 입력 검증 실패(422)인지 */
export function isValidationFailure(e: unknown): boolean {
  return failureResponseOf(e)?.status === 422;
}

function isValidationIssue(value: unknown): value is ValidationIssue {
  const issue = value as Partial<ValidationIssue> | null;
  return (
    typeof issue === "object" &&
    issue !== null &&
    typeof issue.field === "string" &&
    Array.isArray(issue.path) &&
    typeof issue.type === "string" &&
    (issue.limit === undefined || typeof issue.limit === "number")
  );
}

/** 422 응답의 `data.errors`. 422 가 아니거나 옛 서버(`data` 없음)면 빈 배열 */
export function validationErrorsOf(e: unknown): ValidationIssue[] {
  const response = failureResponseOf(e);
  if (response?.status !== 422) return [];
  const errors = (response.data as { data?: { errors?: unknown } } | undefined)
    ?.data?.errors;
  return Array.isArray(errors) ? errors.filter(isValidationIssue) : [];
}

/** 422 `data.errors` 가 서버 상한(50건)에서 잘렸는지(`data.truncated`) */
export function isValidationTruncated(e: unknown): boolean {
  const body = failureResponseOf(e)?.data as
    { data?: { truncated?: unknown } } | undefined;
  return body?.data?.truncated === true;
}

/** 한 건의 원인 문구(문장 끝 마침표 없음). 칸 아래 · 「라벨: 」 뒤 · 「입력값을 확인해 주세요 — 」 뒤 어디에 붙여도 읽히게 쓴다. */
function validationReason({ type, limit }: ValidationIssue): string {
  switch (type) {
    case "string_too_long":
      return limit === undefined
        ? "너무 깁니다"
        : `${String(limit)}자 이하만 입력할 수 있습니다`;
    case "too_long":
      return limit === undefined
        ? "항목이 너무 많습니다"
        : `${String(limit)}개 이하만 등록할 수 있습니다`;
    case "string_too_short":
      return limit === undefined || limit <= 1
        ? "입력이 필요합니다"
        : `${String(limit)}자 이상 입력해야 합니다`;
    case "too_short":
      return `${String(limit ?? 1)}개 이상 필요합니다`;
    case "missing":
      return "입력이 필요합니다";
    // 서버는 Decimal · 날짜 범위면 `limit` 을 싣지 않는다(73차 CZ73-04 — 예전엔 「undefined 이상이어야 합니다」 가 됐다).
    case "greater_than_equal":
      return limit === undefined
        ? "허용 범위를 벗어났습니다"
        : `${String(limit)} 이상이어야 합니다`;
    case "greater_than":
      return limit === undefined
        ? "허용 범위를 벗어났습니다"
        : `${String(limit)}보다 커야 합니다`;
    case "less_than_equal":
      return limit === undefined
        ? "허용 범위를 벗어났습니다"
        : `${String(limit)} 이하여야 합니다`;
    case "less_than":
      return limit === undefined
        ? "허용 범위를 벗어났습니다"
        : `${String(limit)}보다 작아야 합니다`;
    case "literal_error":
    case "enum":
      return "허용된 선택지가 아닙니다";
    case "json_invalid":
      return "요청 형식이 올바르지 않습니다";
    case "extra_forbidden":
      return "허용되지 않은 항목입니다";
    default:
      // value_error(서버 검증자 · 이메일 형식) · *_parsing · *_type · 패턴 불일치 등
      return "형식이 올바르지 않습니다";
  }
}

/**
 * 칸 라벨. 정확한 `field` → 숫자 자리를 `*` 로 바꾼 키(`invites.*.email`) → 마지막 이름(`email`) 순으로 찾는다.
 * 뒤의 두 방법으로 찾았고 경로에 목록 순번이 있으면 「(N번째)」 로 붙인다 — 순번이 둘 이상(중첩 목록)이면 바깥부터 「(2-4번째)」.
 */
function validationLabel(
  issue: ValidationIssue,
  fieldLabels?: Record<string, string>,
): string | undefined {
  if (!fieldLabels) return undefined;
  if (Object.hasOwn(fieldLabels, issue.field)) return fieldLabels[issue.field];
  const wildcard = issue.path
    .map((part) => (typeof part === "number" ? "*" : part))
    .join(".");
  const lastName = issue.path.findLast((part) => typeof part === "string");
  const label = Object.hasOwn(fieldLabels, wildcard)
    ? fieldLabels[wildcard]
    : lastName !== undefined && Object.hasOwn(fieldLabels, lastName)
      ? fieldLabels[lastName]
      : undefined;
  if (label === undefined) return undefined;
  // 중첩 목록은 겹 번호(`strategicGoals.1.tasks.3` → 「(2-4번째)」) — 첫 순번만 쓰면 다른 행을 가리킨다(72차 리뷰).
  const indexes = issue.path.filter((part) => typeof part === "number");
  return indexes.length === 0
    ? label
    : `${label}(${indexes.map((index) => String(index + 1)).join("-")}번째)`;
}

/**
 * 422 실패의 한 줄 안내(토스트 · 배너). 첫 건을 「라벨: 원인.」 으로, 라벨이 없으면 「입력값을 확인해 주세요 — 원인.」 으로 쓰고
 * 여러 건이면 「(외 N건)」(서버 상한에서 잘렸으면 「(외 N건 이상)」)을 붙인다. `data.errors` 가 없으면 `VALIDATION_FAILURE_FALLBACK`.
 * 영문 서버 `message` · camelCase 키를 그대로 보이지 않는다(72차 CW72-05).
 */
export function validationFailureText(
  e: unknown,
  fieldLabels?: Record<string, string>,
): string {
  const issues = validationErrorsOf(e);
  const [first] = issues;
  if (!first) return VALIDATION_FAILURE_FALLBACK;
  const label = validationLabel(first, fieldLabels);
  const text =
    label === undefined
      ? `입력값을 확인해 주세요 — ${validationReason(first)}.`
      : `${label}: ${validationReason(first)}.`;
  // 서버는 50건까지만 싣고 넘으면 `data.truncated` 를 단다 — 실제 건수는 그보다 많다.
  const more = isValidationTruncated(e) ? " 이상" : "";
  return issues.length > 1 || more !== ""
    ? `${text} (외 ${String(issues.length - 1)}건${more})`
    : text;
}

/**
 * 422 실패를 칸별 문구로(키 = 서버 `field`, 같은 칸 여러 건이면 첫 건). 폼은 이 값을 `FormField :error` 에 올린다
 * (`applyServerFieldErrors` + `focusFirstInvalid` — `src/utils/formErrors.ts`). `fieldLabels` 를 주면 문구 앞에 「라벨: 」 을 붙인다 —
 * 칸 아래가 아니라 묶음 영역(목록 위 안내 등)에 올릴 때만 쓴다.
 */
export function validationFieldErrors(
  e: unknown,
  fieldLabels?: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const issue of validationErrorsOf(e)) {
    if (Object.hasOwn(result, issue.field)) continue;
    const label = validationLabel(issue, fieldLabels);
    const reason = `${validationReason(issue)}.`;
    result[issue.field] = label === undefined ? reason : `${label}: ${reason}`;
  }
  return result;
}

/**
 * 응답 시간 초과로 결과를 확인하지 못했을 때의 안내. 서버(LLM 대기 최대 25초 + 저장)는 끝까지 돌아 커밋했을 수 있으므로
 * "저장되지 않았다" 고 단정하지 않고, 먼저 새로고침해 확인하게 한다 — 곧바로 다시 누르면 멱등이 아닌 POST(보고서 · 알림 등)가
 * 두 번 만들어진다.
 */
export const UNCONFIRMED_OUTCOME_MESSAGE =
  "응답 시간이 초과되어 결과를 확인하지 못했습니다. 새로고침해 반영됐는지 먼저 확인한 뒤, 반영되지 않았을 때만 다시 시도해 주세요.";

/** 쓰기 중 연결이 끊긴 경우(`Network Error`) — 서버가 처리한 뒤 응답만 끊겼을 수 있어 「다시 하라」 고 하지 않는다(83차 JR83-02) */
export const CONNECTION_LOST_MESSAGE =
  "연결이 끊겨 결과를 확인하지 못했습니다. 새로고침해 반영됐는지 먼저 확인한 뒤, 반영되지 않았을 때만 다시 시도해 주세요.";

/** 읽기(GET) 중 연결이 끊긴 경우 — 바뀐 것이 없으므로 바로 다시 시도해도 된다 */
export const CONNECTION_FAILED_MESSAGE =
  "서버에 연결하지 못했습니다. 인터넷 연결을 확인한 뒤 다시 시도해 주세요.";

/** 읽기(GET) 5xx 기본 문장 — 서버 원문 대신 한국어로 고정한다(83차 JR83-09) */
const LOAD_FAILED_MESSAGE =
  "일시적인 오류로 불러오지 못했습니다. 잠시 뒤 다시 시도해 주세요.";

/**
 * 같은 문장은 토스트 한 장으로 합친다(83차 JR83-09) — 토스트 `id` 를 문장으로 둔다. 한 화면의 GET 여러 개가 같은 실패를
 * 내도 같은 토스트가 쌓이지 않는다.
 */
function toastFailure(message: string): void {
  toastError(message, { id: message });
}

/**
 * GET 이 아닌 요청이 연결 끊김(`Network Error`)으로 끝났는지(83차 JR83-02). 서버가 처리했는지 모르는 결과 불명이다.
 * 인터셉터는 이 실패를 던지지 않고 토스트를 띄운 뒤 `status 0` 실패 응답으로 돌려준다 — 화면은 응답에서 `status 0` 이면서
 * 자기 `signal` 이 중단되지 않았을 때(중단됐다면 취소) 이 결과 불명으로 읽고 `CONNECTION_LOST_MESSAGE` 를 쓴다(「취소」 로 읽지 않는다).
 */
export function isConnectionLost(e: unknown): boolean {
  if (!axios.isAxiosError(e) || axios.isCancel(e)) return false;
  if (e.message !== "Network Error" && e.code !== "ERR_NETWORK") return false;
  return (e.config?.method ?? "get").toLowerCase() !== "get";
}

/** 결과 불명 실패의 안내 문장 — 연결 끊김과 시간 초과 · 게이트웨이 오류의 문장이 다르다 */
function unconfirmedOutcomeMessage(e: unknown): string {
  return isConnectionLost(e)
    ? CONNECTION_LOST_MESSAGE
    : UNCONFIRMED_OUTCOME_MESSAGE;
}

/**
 * 결과를 확인하지 못한 실패인지. axios 타임아웃(30초, `ECONNABORTED`·`ETIMEDOUT`)과 서버 본문 없이 게이트웨이가
 * 돌려준 503 · 504(API Gateway 통합 한도 30초 초과 — 본문이 `{"message":"Service Unavailable"}` 뿐이고 `code` 가 없다)가
 * 해당한다. 두 경우 모두 영문 문구만 있어 그대로 띄우면 사용자가 원인을 알 수 없다. 503 `LLM_*` 는 서버가 원인을 말한
 * 실패라 여기에 들지 않는다.
 */
export function isUnconfirmedOutcome(e: unknown): boolean {
  if (!axios.isAxiosError(e) || axios.isCancel(e)) return false;
  if (e.code === "ECONNABORTED" || e.code === "ETIMEDOUT") return true;
  if (isConnectionLost(e)) return true;
  const status = e.response?.status ?? 0;
  const code = (e.response?.data as { code?: unknown } | undefined)?.code;
  return (status === 503 || status === 504) && typeof code !== "string";
}

export function catchError<T>(
  e: unknown,
  alert: boolean = true,
  fieldLabels?: Record<string, string>,
): ApiResponse<T> {
  if (!axios.isAxiosError(e)) {
    logger.error("Non-Axios error:", e);
    if (alert) {
      toastError(
        e instanceof Error ? e.message : "알 수 없는 오류가 발생했습니다",
      );
    }
    throw e;
  }

  if (e.code === "ERR_CANCELED") {
    return {
      status: e.response?.status ?? 0,
      statusText: e.response?.statusText ?? "",
      config: e.config as InternalAxiosRequestConfig,
      headers: e.response?.headers ?? {},
      request: e.request,
      data: e.response?.data as T,
      success: false,
    };
  }

  const status = e.response?.status ?? 0;
  // AI 생성 실패(503 `LLM_*`)는 서버가 원인을 설명한 예상된 실패라 4xx 처럼 실패 응답으로 돌려준다.
  // 던지면 호출부 로딩 상태가 풀리지 않거나 처리되지 않은 예외로 Sentry 에 중복 보고된다
  // (크레딧 소진·미연결은 백엔드가 이미 Sentry 에 보고한다). 호출부는 `if (!success)` 로 분기한다.
  if (
    Math.floor(status / 100) === 4 ||
    isLlmFailure(status, e.response?.data)
  ) {
    logger.warn(e);
    if (alert && isValidationFailure(e)) {
      // 입력 검증 실패(422)는 영문 pydantic 원문 대신 칸 · 원인을 한국어로(72차 CW72-05)
      toastFailure(validationFailureText(e, fieldLabels));
    } else if (alert && e.response?.data?.message) {
      // AI 생성 분당 상한은 서버 고정 문구(「1분 뒤」) 대신 남은 초(70차 CO70-03)
      toastFailure(
        (isAiGenerationLimitFailure(e.response)
          ? llmFailureMessage(e.response)
          : null) ?? e.response.data.message,
      );
    }
    return {
      status,
      statusText: e.response?.statusText ?? "",
      config: e.config as InternalAxiosRequestConfig,
      headers: e.response?.headers ?? {},
      request: e.request,
      data: e.response?.data as T,
      success: false,
    };
  } else {
    logger.error(e);
    if (alert) {
      // 서버 원문(「알 수 없는 시스템 오류」)은 사용자 문장이 아니다 — GET 5xx 는 한국어 고정 문장(JR83-09)
      toastFailure(
        isUnconfirmedOutcome(e)
          ? unconfirmedOutcomeMessage(e)
          : status >= 500
            ? LOAD_FAILED_MESSAGE
            : (e.response?.data?.message ?? e.message),
      );
    }
    throw e;
  }
}

async function executeRequest<T = never, R = T>(
  method: Method,
  url: string,
  data?: T,
  options?: ApiOptions,
): Promise<ApiResponse<R>> {
  const {
    refLoading,
    successAlert,
    successMessage,
    failureAlert,
    failureMessage,
  } = { ...getDefaultOptions(method, options) };

  try {
    if (refLoading) {
      refLoading.value = true;
    }

    const requestConfig = {
      method,
      url,
      data: method !== "get" && method !== "delete" ? data : undefined,
      signal: options?.signal,
      silent: options?.silent === true,
      readLike: options?.readLike === true,
    };

    const _promise = axiosInstance.request<T, ApiResponse<R>>(requestConfig);

    if (method !== "get") {
      // vue-sonner 는 포매터가 undefined 를 돌려줘도 빈 토스트를 만들기 때문에,
      // 토스트를 띄우면 안 되는 경우에는 아예 settle 되지 않는 프라미스를 넘겨 생성 자체를 막는다.
      // - 취소된 요청: 오류가 아니므로 조용히 넘어간다.
      // - 인터셉터가 이미 처리한 실패(401 리다이렉트, 네트워크 오류 토스트): 중복 알림을 막는다.
      const _toastPromise = _promise
        .then((response) =>
          response.success ? response : new Promise<never>(() => undefined),
        )
        .catch((e: unknown) => {
          if (axios.isCancel(e)) {
            return new Promise<never>(() => undefined);
          }
          throw e;
        });

      toast.promise(
        _toastPromise,
        getPromiseMessage(
          successMessage ??
            DEFAULT_SUCCESS_MESSAGES[method] ??
            "처리되었습니다.",
          successAlert,
          failureAlert,
          failureMessage,
          options?.fieldLabels,
        ),
      );
    }

    return await _promise;
  } catch (e) {
    // GET 이 아니면 위 `toast.promise` 가 실패 문구를 이미 띄웠다(`failureAlert` 가 켜진 경우).
    // 여기서 또 띄우면 같은 문구가 두 번 쌓이므로 GET 일 때만 catchError 가 알린다.
    const alertedByToast = method !== "get" && failureAlert === true;
    if (alertedByToast && axios.isAxiosError(e) && isUnconfirmedOutcome(e)) {
      // 결과를 모르는 실패(시간 초과 · 게이트웨이 503/504)를 이미 알렸으면 던지지 않고 실패 응답으로 끝낸다. 기본 옵션
      // 호출처 상당수(레퍼런스 질문 생성 · AI 판정 제안 등)가 catch 없이 `if (success)` 만 보므로, 던지면 Vue
      // errorHandler 까지 올라간다. `failureAlert: false` 호출은 각 화면이 이 예외를 잡아 "결과 불명" 안내 ·
      // 다시 불러오기 배너를 띄우므로(status 0 을 "취소" 로 보고 조용히 넘기는 화면이 있어) 그대로 던진다.
      logger.error(e);
      // 던지지 않으므로 오류 이벤트가 생기지 않는다. 게이트웨이 한도 초과 빈도를 보려고 warning 으로 남긴다.
      reportUnconfirmedOutcome(
        method,
        e.config?.url ?? url,
        e.response ? "gateway" : "timeout",
      );
      return toFailureResponse<R>(e);
    }
    return catchError<R>(
      e,
      method === "get" && failureAlert === true,
      options?.fieldLabels,
    );
  } finally {
    if (refLoading) {
      refLoading.value = false;
    }
  }
}

export async function getApi<T = never, R = T>(
  url: string,
  options?: ApiOptions,
): Promise<ApiResponse<R>> {
  return executeRequest<T, R>("get", url, undefined, options);
}

export async function postApi<T = never, R = T>(
  url: string,
  data: T,
  options?: ApiOptions,
): Promise<ApiResponse<R>> {
  return executeRequest<T, R>("post", url, data, options);
}

export async function putApi<T = never, R = T>(
  url: string,
  data: T,
  options?: ApiOptions,
): Promise<ApiResponse<R>> {
  return executeRequest<T, R>("put", url, data, options);
}

export async function patchApi<T = never, R = T>(
  url: string,
  data: T,
  options?: ApiOptions,
): Promise<ApiResponse<R>> {
  return executeRequest<T, R>("patch", url, data, options);
}

export async function deleteApi<T = never, R = T>(
  url: string,
  options?: ApiOptions,
): Promise<ApiResponse<R>> {
  return executeRequest<T, R>("delete", url, undefined, options);
}

export function stringifyParams(obj: Record<string, unknown>): string {
  const sanitizedObject = Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim() !== "";
      if (typeof value === "number") return !isNaN(value);
      if (typeof value === "boolean") return true;
      return value !== "";
    }),
  );

  return stringify(sanitizedObject, {
    arrayFormat: "repeat",
    skipNulls: true,
  });
}
